import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { copyToClipboard } from '../utils/diaryUtils';

// Fallback clipboard function for non-HTTPS environments
const fallbackCopyText = (text) => {
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    if (!successful) {
      sessionStorage.setItem('diaryText', text);
    }
    return successful;
  } catch (err) {
    try {
      sessionStorage.setItem('diaryText', text);
      return true;
    } catch (storageErr) {
      return false;
    }
  }
};

const DEFAULT_PARENT_NOTE = `इन विद्यार्थियों का आज का गृहकार्य अधूरा है। अभिभावकों से सविनय निवेदन है कि कृपया बच्चों का गृहकार्य पूर्ण करवाएं।
(The homework for these students is pending today. Parents are kindly requested to ensure it is completed.)`;

function EnhancedTeacherDiary({ userClass, userName, onClose, isFullView = false }) {
  const [customParentNote, setCustomParentNote] = useState(DEFAULT_PARENT_NOTE);
  const [diary, setDiary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [whatsappLinks, setWhatsappLinks] = useState({ groupLink: '', parentNumbers: [] });
  const [error, setError] = useState(null);
  const [entries, setEntries] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date().toISOString().split('T')[0]);
  const [note, setNote] = useState('Filled by teacher');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Optional pending-homework tracker
  const [classSubjects, setClassSubjects] = useState([]); // [{ name, displayName }] from marksheet/curriculum
  const [classStudents, setClassStudents] = useState([]);
  const [pendingHomework, setPendingHomework] = useState({}); // { studentId: { studentName, subjects: [] } }
  const [showHwTracker, setShowHwTracker] = useState(false);
  const [activeStudentId, setActiveStudentId] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveFileName, setSaveFileName] = useState('Teacher_Diary.png');
  const [imageBusy, setImageBusy] = useState(''); // key of the image action currently generating ('' = idle)
  const scrollRef = useRef(null);
  const [nearBottom, setNearBottom] = useState(false);

  const handleRootScroll = (e) => {
    const el = e.currentTarget;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 120;
    setNearBottom((prev) => (prev === atBottom ? prev : atBottom));
  };

  const handleQuickScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: nearBottom ? 0 : el.scrollHeight, behavior: 'smooth' });
  };

  // School-wide font settings (managed by principal/admin/admin1). 1.5 = +50% for mobile readability.
  const [fontSettings, setFontSettings] = useState({ baseScale: 1.5, subjectTitleSize: '20px', contentFontSize: '17px' });
  const subjectsRef = useRef([]);

  const fetchFontSettings = async () => {
    try {
      const r = await axios.get(`${API_BASE_URL}/api/diary-settings`);
      if (r.data?.diaryFontSettings) setFontSettings(r.data.diaryFontSettings);
    } catch (e) { /* keep defaults */ }
  };

  // Keep typed homework, add new subjects, drop removed ones, follow renamed display names
  const reconcileEntries = (list, subjects) => {
    const byName = {};
    (list || []).forEach(e => { byName[e.subject] = e; });
    return subjects.map(s => ({ subject: s.name, displayName: s.displayName, homework: byName[s.name]?.homework || '' }));
  };

  const refreshSubjects = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/teacher-diary/${userClass}`, { headers: { 'Cache-Control': 'no-cache' } });
      const subjects = response.data?.subjects || [];
      const sig = JSON.stringify(subjects.map(s => [s.name, s.displayName]));
      if (sig === JSON.stringify(subjectsRef.current.map(s => [s.name, s.displayName]))) return;
      subjectsRef.current = subjects;
      setDiary(response.data);
      setEntries(prev => reconcileEntries(prev, subjects));
    } catch (e) { /* ignore transient errors / 404 */ }
  };

  useEffect(() => {
    fetchDiary();
    fetchWhatsAppLinks();
    loadTodaysEntry();
    fetchClassSubjectsAndStudents();
    fetchFontSettings();
  }, [userClass]);

  // Subject / font changes made from the principal dashboard show up without reopening the diary
  useEffect(() => {
    const sync = () => { refreshSubjects(); fetchFontSettings(); };
    const onVisible = () => { if (document.visibilityState === 'visible') sync(); };
    const timer = setInterval(sync, 30000);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', sync);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', sync);
    };
  }, [userClass]);

  // Subjects come from the class marksheet template (falls back to class subjects)
  const fetchClassSubjectsAndStudents = async () => {
    try {
      let subjects = [];
      try {
        const t = await axios.get(`${API_BASE_URL}/api/marksheet-template/${encodeURIComponent(userClass)}`);
        subjects = t.data?.subjects || [];
      } catch (e) {
        subjects = [];
      }
      if (subjects.length === 0) {
        const r = await axios.get(`${API_BASE_URL}/api/class-subjects/${encodeURIComponent(userClass)}`);
        subjects = r.data || [];
      }
      setClassSubjects(subjects.map(s => ({ name: s.name, displayName: s.displayName || s.name })));
    } catch (e) {
      console.error('Error fetching class subjects:', e);
      setClassSubjects([]);
    }
    try {
      const s = await axios.get(`${API_BASE_URL}/api/users?role=student&class=${encodeURIComponent(userClass)}`);
      setClassStudents((s.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } catch (e) {
      console.error('Error fetching students:', e);
      setClassStudents([]);
    }
  };

  const recordsToMap = (records = []) => {
    const map = {};
    records.forEach(r => {
      const key = r.studentId ? String(r.studentId) : r.studentName;
      map[key] = { studentName: r.studentName, subjects: r.subjects || [] };
    });
    return map;
  };

  const getPendingRecords = () =>
    Object.entries(pendingHomework)
      .filter(([, v]) => v.subjects.length > 0)
      .map(([id, v]) => ({ studentId: id, studentName: v.studentName, subjects: v.subjects }));

  const toggleStudentSubject = (student, subjectName) => {
    setPendingHomework(prev => {
      const current = prev[student._id]?.subjects || [];
      const subjects = current.includes(subjectName) ? current.filter(s => s !== subjectName) : [...current, subjectName];
      const next = { ...prev };
      if (subjects.length === 0) delete next[student._id];
      else next[student._id] = { studentName: student.name, subjects };
      return next;
    });
  };

  const loadTodaysEntry = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/enhanced-diary/${userClass}/${currentDate}`);
      if (response.data && response.data.entries) {
        setEntries(subjectsRef.current.length ? reconcileEntries(response.data.entries, subjectsRef.current) : response.data.entries);
        setPendingHomework(recordsToMap(response.data.pendingHomeworkRecords));
        setCustomParentNote(response.data.customParentNote || DEFAULT_PARENT_NOTE);
      }
    } catch (error) {
      // No entry found for today, keep default entries
      console.log('No existing entry for today');
    }
  };

  const saveEntries = async () => {
    try {
      setIsSaving(true);
      setSaveMessage('');
      
      // Save all entries, even if empty (to maintain structure)
      const entriesToSave = entries.map(entry => ({
        subject: entry.subject,
        displayName: entry.displayName,
        homework: entry.homework || ''
      }));

      console.log('Saving diary entries:', {
        className: userClass,
        teacherName: userName,
        date: currentDate,
        entries: entriesToSave,
        note: note
      });

      const response = await axios.post(`${API_BASE_URL}/api/enhanced-diary`, {
        className: userClass,
        teacherName: userName,
        date: currentDate,
        entries: entriesToSave,
        note: note,
        pendingHomeworkRecords: getPendingRecords(),
        customParentNote: customParentNote
      });
      
      console.log('Save response:', response.data);
      setSaveMessage('✅ Diary saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error saving entries:', error);
      console.error('Error details:', error.response?.data);
      setSaveMessage('❌ Error saving diary. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const fetchDiary = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/teacher-diary/${userClass}`);
      setDiary(response.data);
      subjectsRef.current = response.data?.subjects || [];
      
      // Initialize entries with subjects
      if (response.data?.subjects) {
        const initialEntries = response.data.subjects.map(subject => ({
          subject: subject.name,
          displayName: subject.displayName,
          homework: ''
        }));
        setEntries(initialEntries);
      }
      setError(null);
    } catch (error) {
      console.error('Error fetching diary:', error);
      if (error.response?.status === 404) {
        setDiary({ className: userClass, subjects: [], assignments: [] });
        setError(null);
      } else {
        setError('Failed to load diary. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchWhatsAppLinks = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/whatsapp-links/${userClass}`);
      setWhatsappLinks(response.data);
    } catch (error) {
      console.error('Error fetching WhatsApp links:', error);
      setWhatsappLinks({ groupLink: '', parentNumbers: [] });
    }
  };

  const updateEntry = (index, field, value) => {
    const updatedEntries = [...entries];
    updatedEntries[index][field] = value;
    setEntries(updatedEntries);
  };

  const generateDiaryText = () => {
    const date = new Date(currentDate).toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    let text = `🏫 THE RISING SCHOOL\n**DAILY DIARY**\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
    text += `📅 Date: ${date}\n`;
    text += `👨🏫 Teacher: ${userName}\n`;
    text += `📚 Class: ${userClass}\n\n`;

    let hasEntries = false;
    entries.forEach((entry, index) => {
      if (entry.homework) {
        hasEntries = true;
        text += `${index + 1}.   SUBJECT: ${entry.displayName}\n`;
        text += `   ${entry.homework}\n\n`;
      }
    });

    if (!hasEntries) {
      text += `📝 No entries added for ${date}.\n\n`;
    }

    // Pending homework report: only when the teacher has tracked something
    const pendingRecords = getPendingRecords();
    if (pendingRecords.length > 0) {
      text += `━━━━━━━━━━━━━━━━━━━━\n`;
      text += `⚠️ *Pending Homework Report*\n`;
      pendingRecords.forEach(r => {
        const subjectNames = r.subjects.map(sn => classSubjects.find(s => s.name === sn)?.displayName || sn);
        text += `• ${r.studentName}: [${subjectNames.join(', ')}]\n`;
      });
      text += `\n📌 *Note for Parents:*\n`;
      text += `${(customParentNote || '').trim() || DEFAULT_PARENT_NOTE}\n`;
    }

    text += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    text += `**${note}**\n`;
    return text;
  };

  // ── Server-driven image flow: Puppeteer renders the PNG, the phone only shares it (no canvas / base64 / blob URLs) ──
  const toAbsoluteUrl = (u) => (/^https?:/i.test(u) ? u : `${API_BASE_URL || window.location.origin}${u}`);

  // Generating the card/image counts as submitting the diary: persist it in the background (never blocks generation)
  const autoSaveDiaryOnGenerate = async () => {
    try {
      let stored = {};
      try { stored = JSON.parse(localStorage.getItem('user') || '{}') || {}; } catch (e) { stored = {}; }
      await axios.post(`${API_BASE_URL}/api/unified-daily-entry/auto-save-diary`, {
        classId: userClass,
        date: new Date().toISOString(),
        subjects: entries.map(e => ({ subject: e.subject, displayName: e.displayName, homework: e.homework || '' })),
        isAutoSavedOnImageGen: true
      }, {
        headers: {
          'x-user-role': stored.role || 'teacher',
          'x-user-name': encodeURIComponent(userName || stored.name || ''),
          'x-user-class': userClass || stored.class || ''
        },
        timeout: 15000
      });
    } catch (err) {
      console.error('Diary auto-save failed on card generation:', err);
    }
  };

  const requestServerDiaryImage = async () => {
    await autoSaveDiaryOnGenerate();
    const response = await axios.post(`${API_BASE_URL}/api/enhanced-diary/image`, {
      className: userClass,
      teacherName: userName,
      date: currentDate,
      entries,
      note,
      customParentNote,
      pendingHomeworkRecords: getPendingRecords()
    }, { responseType: 'blob', timeout: 45000 });
    const blob = response.data;
    if (!blob || !blob.size || (blob.type && !blob.type.includes('image'))) throw new Error('Empty image response');
    const hosted = response.headers && (response.headers['x-image-url'] || response.headers['X-Image-Url']);
    return { blob, serverImageUrl: hosted ? toAbsoluteUrl(hosted) : null };
  };

  // Generate on the server, then open the native share sheet. Falls back to the hosted image in the external browser.
  const generateAndShareImage = async (label = 'image') => {
    if (imageBusy) return;
    setImageBusy(label);
    try {
      const { blob } = await requestServerDiaryImage();
      const file = new File([blob], 'Teacher_Diary.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: 'The Rising School - Daily Diary', text: generateDiaryText() });
          return;
        } catch (shareErr) {
          if (shareErr && shareErr.name === 'AbortError') return; // user closed the share sheet
          console.warn('Web Share failed:', shareErr);
        }
      }
      // Fallback: download the PNG via a blob anchor, then open WhatsApp so the teacher can attach it
      const fileName = `Diary-${new Date().toISOString().slice(0, 10)}.png`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 10000);

      if (label === 'whatsapp') {
        const target = whatsappLinks.groupLink || `https://wa.me/?text=${encodeURIComponent(generateDiaryText())}`;
        setTimeout(() => {
          const w = window.open(target, '_blank');
          if (!w) window.location.href = target; // popup blocked: navigate instead
        }, 600);
        alert('📱 Image downloaded! WhatsApp is opening - please attach the image from your gallery/downloads.');
      } else {
        alert('📱 Image downloaded!');
      }
    } catch (err) {
      console.error('Diary image error:', err);
      const isServerFail = err && err.response && err.response.status >= 500;
      if (label === 'whatsapp' && isServerFail && whatsappLinks.groupLink) {
        // Image service is down: fall back to sending the diary as text so the teacher is never stuck
        alert('⚠️ Image service is unavailable right now. Sharing the diary as text instead.');
        shareViaWhatsApp('group');
      } else {
        alert('❌ Failed to generate diary image. Please check your connection and try again.');
      }
    } finally {
      setImageBusy('');
    }
  };

  // Save & Download flow: rename prompt, then the static backend URL ending in .png
  const DEFAULT_DOWNLOAD_NAME = 'Teacher_Diary.png';

  const openSaveModal = () => {
    setSaveFileName(DEFAULT_DOWNLOAD_NAME);
    setShowSaveModal(true);
  };

  const confirmSaveDownload = async () => {
    if (imageBusy) return;
    let fileName = String(saveFileName || '').trim() || DEFAULT_DOWNLOAD_NAME;
    fileName = /\.png$/i.test(fileName) ? fileName : `${fileName}.png`;
    let sanitizedFileName = fileName.replace(/\s+/g, '_').replace(/[^\w.\-]/g, '_');
    if (sanitizedFileName === '.png') sanitizedFileName = DEFAULT_DOWNLOAD_NAME;
    const ok = await downloadServerImage(sanitizedFileName);
    if (ok) setShowSaveModal(false);
  };

  const downloadServerImage = async (fileName = DEFAULT_DOWNLOAD_NAME) => {
    if (imageBusy) return false;
    setImageBusy('download');
    try {
      const { serverImageUrl } = await requestServerDiaryImage();
      const match = serverImageUrl && serverImageUrl.match(/\/api\/diary-image\/([^/?#]+)/);
      const imageId = match && match[1];
      if (!imageId) throw new Error('Missing hosted image id');
      const downloadUrl = `${API_BASE_URL || window.location.origin}/api/diary-download/${imageId}/${encodeURIComponent(fileName)}`;
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.setAttribute('download', fileName);
      document.body.appendChild(a);
      a.click();
      setTimeout(() => document.body.removeChild(a), 200);
      // Android WebView fallback: navigate so the native download manager handles it
      if (/Android/i.test(navigator.userAgent)) {
        window.location.href = downloadUrl;
      }
      return true;
    } catch (err) {
      console.error('Diary image download error:', err);
      alert('❌ Failed to generate diary image. Please try again.');
      return false;
    } finally {
      setImageBusy('');
    }
  };

  const imageBtnLabel = (key, idleLabel) => (imageBusy === key ? '⏳ Generating...' : idleLabel);
//__SERVER_IMAGE_END__
  const shareViaWhatsApp = async (type, number = null) => {
    const text = generateDiaryText();
    const encodedText = encodeURIComponent(text);
    
    if (type === 'number' && number) {
      const phone = number.replace(/\D/g, '');
      window.open(`https://wa.me/${phone}?text=${encodedText}`, '_blank');
    } else if (type === 'group' && whatsappLinks.groupLink) {
      window.open(whatsappLinks.groupLink, '_blank');
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(text);
          alert('📋 Diary text copied to clipboard! Paste it in the WhatsApp group.');
        } catch (err) {
          fallbackCopyText(text);
          alert('📱 WhatsApp group opened. Text ready to paste manually.');
        }
      } else {
        fallbackCopyText(text);
        alert('📱 WhatsApp group opened. Please copy and paste the diary text manually.');
      }
    }
  };

  // Image share to WhatsApp: the native share sheet lets the teacher pick the group / contact directly
  const shareImageViaWhatsApp = () => generateAndShareImage('whatsapp');

  const downloadDiary = async (format = 'text') => {
    try {
      {
        const text = generateDiaryText();
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `diary_${userClass}_${currentDate}.txt`;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 100);
        alert('📄 Diary text file downloaded!');
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('❌ Download failed. Please try again.');
    }
  };

  if (loading) {
    return (
      <div style={{ width: '100%', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#f8fafc' }}>
        <div style={{ background: 'white', padding: '40px', borderRadius: '20px', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}> </div>
          <div style={{ fontSize: '18px', color: '#666' }}>Loading Teacher's Diary...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ width: '100%', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#f8fafc' }}>
        <div style={{ background: 'white', padding: '40px', borderRadius: '20px', textAlign: 'center', maxWidth: '400px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
          <h3 style={{ color: '#f44336', marginBottom: '15px' }}>Error Loading Diary</h3>
          <p style={{ marginBottom: '20px', color: '#666' }}>{error}</p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button onClick={() => { setError(null); fetchDiary(); }} style={{ background: '#2196f3', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold' }}>
              🔄 Retry
            </button>
            <button onClick={onClose} style={{ background: '#6c757d', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold' }}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!diary?.subjects?.length) {
    return (
      <div style={{ width: '100%', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#f8fafc' }}>
        <div style={{ background: 'white', padding: '40px', borderRadius: '20px', textAlign: 'center', maxWidth: '500px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}> </div>
          <h3 style={{ color: '#f44336', marginBottom: '15px' }}>No Diary Template Found</h3>
          <p style={{ marginBottom: '20px', color: '#666' }}>
            The Admin needs to set up subjects for Class {userClass} before you can use the diary.
          </p>
          <button onClick={onClose} style={{ background: '#2196f3', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold' }}>
            Close
          </button>
        </div>
      </div>
    );
  }

  // Reload (or reset) everything that depends on the selected date
  const handleDateChange = async (dateValue) => {
    setCurrentDate(dateValue);
    const resetForm = () => {
      setPendingHomework({});
      setCustomParentNote(DEFAULT_PARENT_NOTE);
      if (diary?.subjects) {
        setEntries(diary.subjects.map(subject => ({
          subject: subject.name,
          displayName: subject.displayName,
          homework: ''
        })));
        setNote('Filled by teacher');
      }
    };
    try {
      const response = await axios.get(`${API_BASE_URL}/api/enhanced-diary/${userClass}/${dateValue}`);
      if (response.data && response.data.entries) {
        setEntries(reconcileEntries(response.data.entries, diary?.subjects || subjectsRef.current));
        setNote(response.data.note || 'Filled by teacher');
        setCustomParentNote(response.data.customParentNote || DEFAULT_PARENT_NOTE);
        setPendingHomework(recordsToMap(response.data.pendingHomeworkRecords));
      } else {
        resetForm();
      }
    } catch (err) {
      resetForm();
    }
  };

  const handleCopyImage = () => generateAndShareImage('copy');

  // View: server renders the PNG, then it opens from its hosted URL (no data: URLs, no document.write)
  const handleViewImage = async () => {
    if (imageBusy) return;
    setImageBusy('view');
    try {
      // POST renders the PNG and returns its temporary hosted URL (GET /api/diary-image/:id)
      const { serverImageUrl } = await requestServerDiaryImage();
      if (!serverImageUrl) throw new Error('Missing hosted image URL');
      const w = window.open(serverImageUrl, '_blank');
      if (!w) window.location.href = serverImageUrl; // popup blocked on mobile: navigate instead
    } catch (err) {
      console.error('View image error:', err);
      alert('❌ Failed to open image. Please try again.');
    } finally {
      setImageBusy('');
    }
  };

  // Tailwind preflight is disabled in this project, so box-sizing, borders and focus rings are defined here (scoped to .etd-root).
  const ETD_CSS = `
    @keyframes etd-spin { to { transform: rotate(360deg); } }
    .etd-root, .etd-root * { box-sizing: border-box; }
    .etd-root { height: 100vh; height: 100dvh; overflow-y: auto !important; -webkit-overflow-scrolling: touch; overscroll-behavior-y: contain; touch-action: pan-y; scrollbar-width: thin; scrollbar-color: #94a3b8 #f1f5f9; font-family: system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif; }
    .etd-root::-webkit-scrollbar { width: 8px; height: 8px; }
    .etd-root::-webkit-scrollbar-track { background: #f1f5f9; }
    .etd-root::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 9999px; min-height: 48px; }
    .etd-root::-webkit-scrollbar-thumb:hover, .etd-root::-webkit-scrollbar-thumb:active { background: #64748b; }
    .etd-main { padding-bottom: 120px !important; }
    .etd-fab { position: fixed; right: 16px; bottom: 24px; width: 46px; height: 46px; border-radius: 9999px; background: rgba(30,64,175,.85); color: #fff; font-size: 20px; line-height: 1; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(15,23,42,.3); z-index: 1001; }
    .etd-input { display: block; width: 100%; max-width: 100%; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; font-size: 16px; line-height: 1.5; background: #fff; color: #0f172a; font-family: inherit; outline: none; margin: 0; }
    .etd-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 2px rgba(59,130,246,.5); }
    .etd-input::placeholder { color: #94a3b8; }
    textarea.etd-input { resize: vertical; min-height: 110px; }
    .etd-btn { border: 0; cursor: pointer; font-family: inherit; -webkit-tap-highlight-color: transparent; }
    .etd-btn:active { transform: scale(.97); }
    .etd-btn:disabled { opacity: .6; cursor: not-allowed; }
    .etd-card { background: #fff; border-radius: 16px; padding: 16px; margin-bottom: 12px; border: 1px solid #f1f5f9; box-shadow: 0 1px 2px rgba(15,23,42,.06); }
  `;

  // Font scale from school settings. Defaults (20px title / 17px content) already include the +50% boost at 1.5x.
  const fontScale = Math.min(2.2, Math.max(1, Number(fontSettings.baseScale) || 1.5));
  const relScale = fontScale / 1.5;
  const sz = (n) => `${Math.round(n * fontScale * 10) / 10}px`;
  const titleSize = `${Math.round((parseFloat(fontSettings.subjectTitleSize) || 20) * relScale * 10) / 10}px`;
  const contentSize = `${Math.round((parseFloat(fontSettings.contentFontSize) || 17) * relScale * 10) / 10}px`;

  const prettyDate = new Date(currentDate).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const pendingCount = getPendingRecords().length;

  return (
    <div
      ref={scrollRef}
      onScroll={handleRootScroll}
      className="etd-root fixed inset-0 w-full bg-slate-50 z-50 flex flex-col"
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        width: '100%',
        height: '100dvh',
        background: '#f8fafc',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        overscrollBehaviorY: 'contain'
      }}
    >
      <style>{ETD_CSS}</style>

      {/* Header */}
      <header
        className="relative shrink-0 p-4 md:p-5 text-white"
        style={{ position: 'relative', background: 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)', padding: '16px 56px 16px 16px', flexShrink: 0, color: 'white' }}
      >
        <button
          type="button"
          aria-label="Close diary"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
          className="etd-btn absolute top-3 right-3 z-10 flex items-center justify-center rounded-full text-white text-base font-bold"
          style={{ position: 'absolute', top: '12px', right: '12px', width: '36px', height: '36px', borderRadius: '50%', color: 'white', fontSize: '16px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#dc2626', boxShadow: '0 2px 6px rgba(0,0,0,.25)', zIndex: 10 }}
        >
          ✕
        </button>
        <div className="flex items-center gap-3 min-w-0" style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          <div className="shrink-0 flex items-center justify-center rounded-full text-2xl" style={{ width: '48px', height: '48px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', flexShrink: 0, background: 'rgba(255,255,255,0.2)' }}>🏫</div>
          <div className="min-w-0 flex-1" style={{ minWidth: 0, flex: 1 }}>
            <h1 className="m-0 text-xl md:text-3xl font-bold leading-tight break-words" style={{ margin: 0, fontSize: '22px', fontWeight: 'bold', lineHeight: 1.2, overflowWrap: 'anywhere' }}>The Rising School</h1>
            <h2 className="m-0 text-sm md:text-xl font-semibold break-words" style={{ margin: '2px 0 0', fontSize: '14px', fontWeight: 600, opacity: 0.95, overflowWrap: 'anywhere' }}>Teacher's Diary</h2>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
          <span className="rounded-full px-3 py-1 text-xs font-medium break-words" style={{ borderRadius: '99px', padding: '4px 12px', fontSize: '12px', fontWeight: 500, maxWidth: '100%', overflowWrap: 'anywhere', background: 'rgba(255,255,255,0.18)' }}>📚 Class: {userClass}</span>
          <span className="rounded-full px-3 py-1 text-xs font-medium break-words" style={{ borderRadius: '99px', padding: '4px 12px', fontSize: '12px', fontWeight: 500, maxWidth: '100%', overflowWrap: 'anywhere', background: 'rgba(255,255,255,0.18)' }}>👨‍🏫 {userName}</span>
        </div>
      </header>

      {/* Body: scrolls with the window only - no nested scroll containers */}
      <main className="etd-main w-full max-w-3xl mx-auto p-3 md:p-6 pb-32" style={{ width: '100%', maxWidth: '768px', margin: '0 auto', padding: window.innerWidth < 768 ? '12px 12px 112px' : '24px 24px 112px', flexShrink: 0 }}>

        {/* Date + primary actions */}
        <section className="etd-card">
          <label className="block mb-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide" style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#64748b' }}>📅 DATE</label>
          <input type="date" className="etd-input" value={currentDate} onChange={(e) => handleDateChange(e.target.value)} />
          <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#64748b' }}>{prettyDate}</p>

          <div className="grid grid-cols-3 gap-2 mt-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px', marginTop: '12px' }}>
            {[
              { key: 'save', icon: '💾', label: isSaving ? 'Saving…' : 'Save Diary', bg: isSaving ? '#6b7280' : 'linear-gradient(135deg, #059669 0%, #047857 100%)', onClick: saveEntries, disabled: isSaving },
              { key: 'text', icon: '📋', label: 'Copy Text', bg: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)', onClick: () => copyToClipboard(generateDiaryText(), 'Diary copied to clipboard!', 'Diary text ready to paste') },
              { key: 'img', icon: '🖼️', label: imageBtnLabel('copy', 'Copy Image'), bg: imageBusy ? '#6b7280' : 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', onClick: handleCopyImage, disabled: !!imageBusy }
            ].map(b => (
              <button
                key={b.key}
                type="button"
                onClick={b.onClick}
                disabled={b.disabled}
                className="etd-btn flex flex-col items-center justify-center gap-1 rounded-xl py-3 px-1 text-xs font-semibold text-white shadow-md"
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', borderRadius: '12px', padding: '12px 4px', fontSize: '12px', fontWeight: 600, color: 'white', boxShadow: '0 4px 8px rgba(15,23,42,.15)', minHeight: '64px', background: b.bg }}
              >
                <span style={{ fontSize: '18px', lineHeight: 1 }}>{b.icon}</span>
                <span>{b.label}</span>
              </button>
            ))}
          </div>

          {saveMessage && (
            <div style={{ marginTop: '12px', borderRadius: '12px', padding: '8px', fontSize: '12px', fontWeight: 'bold', textAlign: 'center', background: saveMessage.includes('✅') ? '#dcfce7' : '#fee2e2', color: saveMessage.includes('✅') ? '#059669' : '#dc2626' }}>
              {saveMessage}
            </div>
          )}
        </section>

        {/* Subject cards */}
        <h3 style={{ margin: '16px 4px 8px', fontSize: sz(14), fontWeight: 'bold', color: '#334155' }}>📝 Daily Homework Assignment</h3>
        {entries.map((entry, index) => (
          <section key={entry.subject} className="etd-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', minWidth: 0 }}>
              <span style={{ width: sz(32), height: sz(32), borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#2563eb', color: 'white', fontSize: sz(14), fontWeight: 'bold' }}>{index + 1}</span>
              <h4 style={{ margin: 0, minWidth: 0, flex: 1, fontSize: titleSize, fontWeight: 'bold', color: '#1e40af', overflowWrap: 'anywhere' }}>📚 {entry.displayName}</h4>
            </div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: sz(12), fontWeight: 600, color: '#475569' }}>Homework Assigned</label>
            <textarea
              className="etd-input"
              style={{ fontSize: contentSize }}
              rows={4}
              value={entry.homework}
              onChange={(e) => updateEntry(index, 'homework', e.target.value)}
              placeholder={`What homework did you assign for ${entry.displayName}?`}
            />
          </section>
        ))}

        {/* Optional: Student Homework Status (defaulters list) */}
        <section className="etd-card" style={{ background: '#fff7ed', borderColor: '#fed7aa' }}>
          <button
            type="button"
            onClick={() => setShowHwTracker(v => !v)}
            className="etd-btn"
            style={{ width: '100%', textAlign: 'left', background: 'transparent', color: '#c2410c', padding: 0, fontSize: '14px', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}
          >
            <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>📋 Student Homework Status (Optional Defaulters List){pendingCount > 0 ? ` • ${pendingCount}` : ''}</span>
            <span style={{ flexShrink: 0 }}>{showHwTracker ? '▲' : '▼'}</span>
          </button>
          {showHwTracker && (
            <div style={{ marginTop: '10px' }}>
              {classStudents.length === 0 && <div style={{ fontSize: '12px', color: '#6b7280' }}>No students found for this class.</div>}
              {classStudents.map(student => {
                const rec = pendingHomework[student._id];
                const open = activeStudentId === student._id;
                return (
                  <div key={student._id} style={{ borderBottom: '1px solid #fde4c8', padding: '6px 0' }}>
                    <div
                      onClick={() => setActiveStudentId(open ? null : student._id)}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px', padding: '4px 2px', flexWrap: 'wrap', minHeight: '32px' }}
                    >
                      <span style={{ fontWeight: 600, color: rec ? '#b91c1c' : '#374151', overflowWrap: 'anywhere' }}>{student.name}</span>
                      {rec && rec.subjects.map(sn => (
                        <span key={sn} style={{ background: '#fef3c7', color: '#92400e', borderRadius: '6px', padding: '1px 8px', fontSize: '11px', fontWeight: 700 }}>{sn}</span>
                      ))}
                    </div>
                    {open && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '4px 2px 6px' }}>
                        {classSubjects.map(sub => {
                          const on = rec?.subjects.includes(sub.name);
                          return (
                            <button
                              type="button"
                              key={sub.name}
                              onClick={() => toggleStudentSubject(student, sub.name)}
                              className="etd-btn"
                              style={{ border: `1px solid ${on ? '#dc2626' : '#cbd5e1'}`, background: on ? '#fee2e2' : '#f8fafc', color: on ? '#b91c1c' : '#475569', borderRadius: '99px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, minHeight: '36px' }}
                            >
                              {sub.displayName}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              {pendingCount > 0 && (
                <button type="button" onClick={() => setPendingHomework({})} className="etd-btn" style={{ marginTop: '8px', background: 'transparent', color: '#6b7280', fontSize: '12px', textDecoration: 'underline' }}>
                  Clear all
                </button>
              )}
            </div>
          )}

          {/* Editable bilingual note for parents */}
          <div style={{ marginTop: '14px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 'bold', color: '#c2410c' }}>
              📝 Custom Note / Message for Parents (Optional):
            </label>
            <textarea
              className="etd-input"
              rows={4}
              value={customParentNote}
              onChange={(e) => setCustomParentNote(e.target.value)}
              placeholder={DEFAULT_PARENT_NOTE}
              style={{ borderColor: '#fdba74', background: '#fffaf5' }}
            />
            <button type="button" onClick={() => setCustomParentNote(DEFAULT_PARENT_NOTE)} className="etd-btn" style={{ marginTop: '6px', background: 'transparent', color: '#6b7280', fontSize: '12px', textDecoration: 'underline', padding: 0 }}>
              Reset to default
            </button>
          </div>
        </section>

        {/* Note Section */}
        <section className="etd-card">
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 'bold', color: '#1565c0' }}>📝 Note:</label>
          <input
            type="text"
            className="etd-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ fontWeight: 'bold', textAlign: 'center', color: '#1565c0', borderColor: '#93c5fd' }}
          />
        </section>

        {/* WhatsApp Sharing */}
        <section className="etd-card" style={{ borderColor: '#86efac' }}>
          <h5 style={{ margin: '0 0 10px', color: '#16a34a', fontSize: '15px', fontWeight: 'bold' }}>📱 Share via WhatsApp</h5>

          {whatsappLinks.groupLink && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px', marginBottom: '10px' }}>
              <button
                type="button"
                onClick={() => {
                  const text = generateDiaryText();
                  copyToClipboard(text, 'Message copied! Now opening WhatsApp group...', 'Message ready to paste in WhatsApp');
                  setTimeout(() => window.open(whatsappLinks.groupLink, '_blank'), 500);
                }}
                className="etd-btn"
                style={{ background: '#25d366', color: 'white', borderRadius: '12px', padding: '12px 6px', fontSize: '12px', fontWeight: 600, boxShadow: '0 2px 6px rgba(15,23,42,.15)' }}
              >
                📝 Text to Group
              </button>
              <button
                type="button"
                disabled={!!imageBusy}
                onClick={() => shareImageViaWhatsApp()}
                className="etd-btn"
                style={{ background: imageBusy ? '#6b7280' : '#128c7e', color: 'white', borderRadius: '12px', padding: '12px 6px', fontSize: '12px', fontWeight: 600, boxShadow: '0 2px 6px rgba(15,23,42,.15)' }}
              >
                {imageBusy === 'whatsapp' ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 12, height: 12, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'etd-spin .8s linear infinite' }} />
                    Generating...
                  </span>
                ) : '🖼️ Image to Group'}
              </button>
            </div>
          )}

          {whatsappLinks.parentNumbers?.length > 0 && whatsappLinks.parentNumbers.slice(0, 2).map((number, index) => (
            <div key={index} style={{ marginBottom: '8px' }}>
              <div style={{ color: '#475569', fontSize: '12px', marginBottom: '4px', overflowWrap: 'anywhere' }}>{number}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px' }}>
                <button type="button" onClick={() => shareViaWhatsApp('number', number)} className="etd-btn" style={{ background: '#25d366', color: 'white', borderRadius: '10px', padding: '9px 6px', fontSize: '12px', fontWeight: 600 }}>📝 Text</button>
                <button type="button" disabled={!!imageBusy} onClick={() => shareImageViaWhatsApp()} className="etd-btn" style={{ background: imageBusy ? '#6b7280' : '#128c7e', color: 'white', borderRadius: '10px', padding: '9px 6px', fontSize: '12px', fontWeight: 600 }}>🖼️ {imageBtnLabel('whatsapp', 'Image')}</button>
              </div>
            </div>
          ))}

          {!whatsappLinks.groupLink && (!whatsappLinks.parentNumbers || whatsappLinks.parentNumbers.length === 0) && (
            <div style={{ textAlign: 'center', color: '#64748b', fontSize: '12px' }}>No WhatsApp contacts configured</div>
          )}
        </section>

        {/* More options */}
        <section className="etd-card" style={{ borderColor: '#bfdbfe' }}>
          <h5 style={{ margin: '0 0 10px', color: '#2563eb', fontSize: '15px', fontWeight: 'bold' }}>💾 Save & Download</h5>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px' }}>
            <button type="button" disabled={!!imageBusy} onClick={shareImageViaWhatsApp} className="etd-btn" style={{ gridColumn: '1 / -1', background: imageBusy ? '#6b7280' : '#25d366', color: 'white', borderRadius: '12px', padding: '14px 4px', fontSize: '14px', fontWeight: 700, boxShadow: '0 2px 6px rgba(15,23,42,.15)' }}>{imageBusy === 'whatsapp' ? '⏳ Generating...' : '📲 Share to WhatsApp'}</button>
            <button type="button" disabled={!!imageBusy} onClick={handleViewImage} className="etd-btn" style={{ background: '#fff', color: '#334155', border: '1px solid #cbd5e1', borderRadius: '12px', padding: '12px 4px', fontSize: '12px', fontWeight: 600 }}>{imageBusy === 'view' ? '⏳ Generating...' : '👁️ View Image'}</button>
            <button type="button" disabled={!!imageBusy} onClick={openSaveModal} className="etd-btn" style={{ background: imageBusy ? '#6b7280' : '#2563eb', color: 'white', borderRadius: '12px', padding: '12px 4px', fontSize: '12px', fontWeight: 600, boxShadow: '0 2px 6px rgba(15,23,42,.15)' }}>{imageBusy === 'download' ? '⏳ Generating...' : '💾 Save & Download'}</button>
            <button type="button" onClick={() => downloadDiary('text')} className="etd-btn" style={{ background: '#ef4444', color: 'white', borderRadius: '12px', padding: '12px 4px', fontSize: '12px', fontWeight: 600, boxShadow: '0 2px 6px rgba(15,23,42,.15)' }}>📄 Text</button>
          </div>
        </section>

        {/* Preview */}
        <section className="etd-card" style={{ borderColor: '#a7f3d0' }}>
          <h5 style={{ margin: '0 0 10px', color: '#059669', fontSize: sz(15), fontWeight: 'bold' }}>👁️ Preview</h5>
          <div style={{ fontSize: contentSize, color: '#475569', background: '#f9fafb', padding: '10px', borderRadius: '12px' }}>
            {entries.some(entry => entry.homework) ? (
              entries.filter(entry => entry.homework).map(entry => (
                <div key={entry.subject} style={{ marginBottom: '8px', overflowWrap: 'anywhere' }}>
                  <strong>{entry.displayName}:</strong>
                  <div>📝 {entry.homework.length > 60 ? `${entry.homework.substring(0, 60)}...` : entry.homework}</div>
                </div>
              ))
            ) : (
              <div style={{ fontStyle: 'italic', color: '#9ca3af' }}>No entries added yet...</div>
            )}
          </div>
        </section>
      </main>

      <button
        type="button"
        className="etd-btn etd-fab"
        aria-label={nearBottom ? 'Scroll to top' : 'Scroll to bottom'}
        onClick={handleQuickScroll}
      >
        {nearBottom ? '↑' : '↓'}
      </button>

      {showSaveModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Save Diary Image"
          onClick={() => !imageBusy && setShowSaveModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 380, padding: 20, boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Save Diary Image</h3>
            <label htmlFor="etd-save-filename" style={{ display: 'block', fontSize: 13, color: '#475569', marginBottom: 6 }}>File name</label>
            <input
              id="etd-save-filename"
              type="text"
              value={saveFileName}
              autoFocus
              onChange={(e) => setSaveFileName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmSaveDownload(); }}
              style={{ width: '100%', boxSizing: 'border-box', padding: '12px', fontSize: 16, border: '1px solid #cbd5e1', borderRadius: 10, outline: 'none' }}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button type="button" disabled={!!imageBusy} onClick={() => setShowSaveModal(false)}
                style={{ flex: 1, padding: '12px 8px', borderRadius: 10, border: '1px solid #cbd5e1', background: '#f1f5f9', color: '#0f172a', fontWeight: 600, fontSize: 14 }}>
                Cancel
              </button>
              <button type="button" disabled={!!imageBusy} onClick={confirmSaveDownload}
                style={{ flex: 1.5, padding: '12px 8px', borderRadius: 10, border: 'none', background: imageBusy ? '#6b7280' : '#10b981', color: '#fff', fontWeight: 700, fontSize: 14 }}>
                {imageBusy === 'download' ? 'Generating...' : 'Download (.png)'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default EnhancedTeacherDiary;
